import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import hashSum from 'hash-sum'
import get from 'lodash/get'

import { TemplateCurrentItemMissingError } from 'apps/templates/errors'
import { templateAdapter } from 'apps/templates/models/Template/adapter'
import { Template } from 'shared/api/common/schemas/templates/template'
import errorTrackingService from 'utils/services/errorTrackingService'

import {
  fetchSingleuseLink,
  fetchTemplate,
  saveDescription,
  saveShare,
  saveTitle,
  setShareType,
  updateTemplateStructureNotification,
} from '../actions/currentTemplateActions'
import { updateTemplateAvatar, updateTemplateNotification } from '../actions/templatesActions'

import { CurrentTemplatesInitialState } from './types'

export const currentTemplateSlice = createSlice({
  name: 'templatesCurrent',
  initialState: CurrentTemplatesInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(updateTemplateStructureNotification, (state, action) => {
      const updatedItem = action.payload
      if (state.item == null || state.item.id !== updatedItem.id) {
        return
      }

      const hash = hashSum(updatedItem)
      const item = { ...state.item, ...updatedItem }
      state.hasChanges = hash !== hashSum(item)
      state.hash = hash
      state.item = item
    })

    builder
      .addCase(fetchTemplate.pending.type, (state) => {
        state.fetching = true
        state.error = null
      })

      .addCase(fetchTemplate.rejected, (state) => {
        state.fetching = false
      })

      .addCase(fetchTemplate.fulfilled.type, (state, action: PayloadAction<Template>) => {
        const result = templateAdapter(action.payload)

        state.item = result
        state.fetching = false
        state.hash = hashSum(result)
        state.hasChanges = false
      })

      .addCase(saveTitle.pending.type, (state) => {
        state.fetchingTitle = true
        state.error = null
      })

      .addCase(saveTitle.rejected.type, (state) => {
        state.fetchingTitle = false
      })

      .addCase(saveTitle.fulfilled.type, (state, action: PayloadAction<Template>) => {
        if (state.item) {
          state.item.title = action.payload.title
        } else {
          errorTrackingService.logWarning(
            new TemplateCurrentItemMissingError('saveTitle is requested'),
            {
              scope: 'root',
              section: null,
            },
          )
        }

        state.fetchingTitle = false
      })

      .addCase(saveShare.pending.type, (state) => {
        state.fetchingShare = true
      })

      .addCase(saveShare.rejected.type, (state) => {
        state.fetchingShare = false
      })

      .addCase(saveShare.fulfilled.type, (state, action: PayloadAction<Template>) => {
        const { is_shared, share_hash, share_link } = action.payload

        if (state.item) {
          state.item.is_shared = is_shared
          state.item.share_hash = share_hash
          state.item.share_link = share_link
        } else {
          errorTrackingService.logWarning(
            new TemplateCurrentItemMissingError('saveShare is requested'),
            {
              scope: 'root',
              section: null,
            },
          )
        }

        state.fetchingShare = false
      })

      .addCase(saveDescription.pending.type, (state) => {
        state.fetchingDescription = true
      })

      .addCase(saveDescription.rejected.type, (state) => {
        state.fetchingDescription = false
      })

      .addCase(saveDescription.fulfilled.type, (state, action: PayloadAction<Template>) => {
        if (state.item) {
          state.item.description = action.payload.description
        } else {
          errorTrackingService.logWarning(
            new TemplateCurrentItemMissingError('saveDescription is requested'),
            {
              scope: 'root',
              section: null,
            },
          )
        }

        state.fetchingDescription = false
      })

      .addCase(
        updateTemplateAvatar.fulfilled.type,
        (state, action: PayloadAction<void, string, { arg: { avatarURL: string } }>) => {
          const { avatarURL } = action.meta.arg

          if (state.item) {
            state.item.image.avatar_url_100x100 = avatarURL
          } else {
            errorTrackingService.logWarning(
              new TemplateCurrentItemMissingError('updateTemplateAvatar is requested'),
              {
                scope: 'root',
                section: null,
              },
            )
          }
        },
      )

      .addCase(updateTemplateNotification, (state, action) => {
        const { current_version, template_id, ...rest } = action.payload.item

        if (state.item) {
          if (template_id === get(state, 'item.template_id')) {
            rest.description = rest.description || state.item.description
            state.item = { ...state.item, ...rest }
          }
        } else {
          errorTrackingService.logWarning(
            new TemplateCurrentItemMissingError('updateTemplateNotification is updated'),
            { scope: 'root', section: null },
          )
        }
      })

      .addCase(fetchSingleuseLink.pending.type, (state) => {
        if (state.item) {
          state.item.isGeneratingLink = true
        } else {
          errorTrackingService.logWarning(
            new TemplateCurrentItemMissingError('fetchSingleuseLink is requested'),
            {
              scope: 'root',
              section: null,
            },
          )
        }
      })

      .addCase(
        fetchSingleuseLink.fulfilled.type,
        (
          state,
          action: PayloadAction<{
            share_hash: string
            share_link: string
            template_id: number
            template_installation_id: number | null
            ts_created: string
            ts_valid_till: string
          }>,
        ) => {
          if (state.item) {
            state.item = { ...state.item, ...action.payload, isGeneratingLink: false }
          } else {
            errorTrackingService.logWarning(
              new TemplateCurrentItemMissingError('fetchSingleuseLink is requested'),
              {
                scope: 'root',
                section: null,
              },
            )
          }
        },
      )

      .addCase(setShareType.fulfilled.type, (state, action: PayloadAction<Template>) => {
        const result = templateAdapter(action.payload)
        state.item = { ...state.item, ...result, isGeneratingLink: false }
      })
  },
})

export const CurrentTemplateReducer = currentTemplateSlice.reducer
